<script lang="ts">
import { computed, defineComponent, watch } from "vue"
import { useRoute, useRouter } from "vue-router/composables"
import { useStore } from "@/utils/vuex-utils"
import { BreadcrumbItem } from "@/types/breadcrumbs"
import { translateForOemToLower, useI18n } from "@/utils/i18n-utils"
import FlexibilityIcon from "@/components/icons/flexibility-icon.vue"
import { getDeviceIcon } from "@/utils"
import { DEFAULT_DOCUMENT_TITLE } from "@/config/router"

export default defineComponent({
    name: "Breadcrumbs",
    components: {
        FlexibilityIcon,
    },
    setup() {
        const router = useRouter()
        const route = useRoute()
        const store = useStore()
        const i18n = useI18n()

        const isInitialized = computed(() => store.getters["breadcrumbs/isInitialized"])
        const categorizedData = computed(() => store.state.breadcrumbs.categorizedData)

        const items = computed(() => store.getters["breadcrumbs/items"])

        const navigateTo = (item: BreadcrumbItem, event: MouseEvent) => {
            event.preventDefault()

            if (!item.doNotVisit) {
                if (item.isContextual) {
                    store.dispatch("navigationContext/clearNavigationContext")
                }

                router.push(item.path)
            }
        }

        const updateBreadcrumbs = () => {
            store.dispatch("breadcrumbs/updateBreadcrumbs", route)
        }

        watch(() => [route.path, route.matched], updateBreadcrumbs, { immediate: true })

        watch(isInitialized, (initialized) => {
            if (initialized) {
                updateBreadcrumbs()
            }
        })

        watch(
            () => categorizedData.value,
            () => {
                if (isInitialized.value) {
                    updateBreadcrumbs()
                }
            },
        )

        const getTranslatedBreadcrumbText = (item: BreadcrumbItem) => {
            if (!item.text) return null

            if (item.type === "dynamic") {
                const siteName = store.getters["siteInfo/productName"]

                return translateForOemToLower(siteName, item.text, i18n) || item.text
            }

            return i18n.t(item.text)
        }

        const filteredItems = computed(() => {
            return items.value.filter((item: any) => getTranslatedBreadcrumbText(item) !== null)
        })

        const getDeviceClass = (device: BreadcrumbItem) => {
            const categoryValue = device.data?.category?.value
            if (!categoryValue) return "icon icon-other"

            return getDeviceIcon(categoryValue)
        }

        const addIconsToBreadcrumbs = (items: BreadcrumbItem[]) => {
            return items.map((item) => {
                let iconClass = ""

                switch (item.type) {
                    case "company":
                        iconClass = "fa fa-xs fa-solid fa-landmark"
                        break
                    case "location-group":
                        iconClass = "fa fa-xs fa-solid fa-sitemap"
                        break
                    case "location":
                        iconClass = "fa fa-xs fa-solid fa-house"
                        break
                    case "device-group":
                    case "integrated-devices":
                        iconClass = "fa fa-xs fa-solid fa-object-ungroup"
                        break
                    case "device":
                        iconClass = getDeviceClass(item)
                        break
                    case "location-group-flexibility":
                        iconClass = "flexibility-icon"
                        break
                    case "location-charts":
                        iconClass = "icon icon-chart"
                        break
                    case "location-strategies":
                        iconClass = "icon icon-energy"
                        break
                    case "location-alarm-settings":
                        iconClass = "icon icon-bell"
                        break
                }
                return { ...item, iconClass }
            })
        }

        const itemsWithIcons = computed(() => {
            return addIconsToBreadcrumbs(filteredItems.value)
        })

        const isLoading = computed(() => store.getters["breadcrumbs/isLoading"])

        watch(itemsWithIcons, (items) => {
            const info = store.getters["reduxi/getSiteInfo"]
            const siteName = info?.platformName || DEFAULT_DOCUMENT_TITLE
            const titles = items
                .map((item) => getTranslatedBreadcrumbText(item))
                .filter(Boolean)
                .map(String)
            document.title = titles.length > 0 ? [...titles, siteName].join(" | ") : siteName
        })

        return {
            filteredItems,
            navigateTo,
            isInitialized,
            i18n,
            getTranslatedBreadcrumbText,
            itemsWithIcons,
            isLoading,
        }
    },
})
</script>

<template>
    <div class="breadcrumbs">
        <b-overlay :show="isLoading" class="new-breadcrumbs-overlay" aria-label="breadcrumb">
            <b-breadcrumb class="mobile-breadcrumbs">
                <b-breadcrumb-item
                    v-if="itemsWithIcons.length > 1"
                    :key="'dropdown'"
                    class="breadcrumb-item"
                >
                    <b-dropdown text="..." variant="link" no-caret menu-class="dropdown-menu-dark">
                        <b-dropdown-item
                            v-for="(item, index) in itemsWithIcons.slice(0, -1)"
                            :key="index"
                            class="dark-dropdown-item"
                            :href="item.path"
                        >
                            <div class="breadcrumb-content" @click="navigateTo(item, $event)">
                                <template v-if="item.iconClass === 'flexibility-icon'">
                                    <FlexibilityIcon
                                        class="icon breadcrumb-icon"
                                        style="height: 15px"
                                    />
                                </template>
                                <template v-else>
                                    <span :class="item.iconClass" class="breadcrumb-icon" />
                                </template>
                                <span class="breadcrumb-title">{{
                                    getTranslatedBreadcrumbText(item)
                                }}</span>
                            </div>
                        </b-dropdown-item>
                    </b-dropdown>
                </b-breadcrumb-item>
                <b-breadcrumb-item
                    v-for="(item, index) in itemsWithIcons.slice(-1)"
                    :key="index"
                    :class="['breadcrumb-item', { active: true }]"
                >
                    <div class="breadcrumb-content">
                        <template v-if="item.iconClass === 'flexibility-icon'">
                            <FlexibilityIcon class="icon breadcrumb-icon" style="height: 15px" />
                        </template>
                        <template v-else>
                            <span :class="item.iconClass" class="breadcrumb-icon" />
                        </template>
                        <span class="breadcrumb-title">{{
                            getTranslatedBreadcrumbText(item)
                        }}</span>
                    </div>
                </b-breadcrumb-item>
            </b-breadcrumb>

            <b-breadcrumb class="desktop-breadcrumbs new-breadcrumbs">
                <b-breadcrumb-item
                    v-if="itemsWithIcons.length > 3"
                    :key="'dropdown'"
                    class="breadcrumb-item"
                >
                    <b-dropdown text="..." variant="link" no-caret menu-class="dropdown-menu-dark">
                        <b-dropdown-item
                            v-for="(item, index) in itemsWithIcons.slice(0, -3)"
                            :key="index"
                            class="dark-dropdown-item"
                            :href="item.path"
                        >
                            <div class="breadcrumb-content" @click="navigateTo(item, $event)">
                                <template v-if="item.iconClass === 'flexibility-icon'">
                                    <FlexibilityIcon
                                        class="icon breadcrumb-icon"
                                        style="height: 15px"
                                    />
                                </template>
                                <template v-else>
                                    <span :class="item.iconClass" class="breadcrumb-icon" />
                                </template>
                                <span class="breadcrumb-title">{{
                                    getTranslatedBreadcrumbText(item)
                                }}</span>
                            </div>
                        </b-dropdown-item>
                    </b-dropdown>
                </b-breadcrumb-item>
                <b-breadcrumb-item
                    v-for="(item, index) in itemsWithIcons.slice(-3)"
                    :key="index"
                    :class="[
                        'breadcrumb-item',
                        'new-breadcrumb-item',
                        { active: index === itemsWithIcons.length - 1 },
                        { 'non-clickable': item.doNotVisit },
                    ]"
                    :href="item.path"
                    @click="navigateTo(item, $event)"
                >
                    <div class="breadcrumb-content">
                        <template v-if="item.iconClass === 'flexibility-icon'">
                            <FlexibilityIcon class="icon breadcrumb-icon" style="height: 15px" />
                        </template>
                        <template v-else>
                            <span :class="item.iconClass" class="breadcrumb-icon" />
                        </template>
                        <span class="breadcrumb-title">{{
                            getTranslatedBreadcrumbText(item)
                        }}</span>
                    </div>
                </b-breadcrumb-item>
            </b-breadcrumb>
        </b-overlay>
    </div>
</template>

<style lang="scss">
.debug-info {
    color: #999;
    font-style: italic;
    padding: 8px;
}
.breadcrumb-item {
    cursor: pointer;
}
.breadcrumb-item.non-clickable {
    cursor: default;
    opacity: 0.7;
    pointer-events: none;
}
.breadcrumb-item.contextual {
    font-style: italic;
    color: var(--theme-primary);
}
.breadcrumb-icon {
    margin-right: 8px;
    font-size: 14px;

    &.icon {
        font-family: "reduxi" !important;
        font-style: normal;
    }
}

.breadcrumb-content {
    display: flex;
    align-items: center;
}

.dark-dropdown-item {
    cursor: pointer;
    &:hover {
        background: var(--theme-primary);
    }
}

.dropdown-menu-dark {
    background: var(--theme-secondary);
    .dropdown-item {
        color: white;
    }
}

@media (min-width: 768px) {
    .mobile-breadcrumbs {
        display: none;
    }
}

@media (max-width: 767px) {
    .desktop-breadcrumbs {
        display: none;
    }
}
</style>
